import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Heart, Menu, X } from 'lucide-react';

const Header: React.FC = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  const toggleLanguage = () => {
    i18n.changeLanguage(i18n.language === 'en' ? 'ja' : 'en');
  };

  const navItems = [
    { key: 'home', to: '/' },
    { key: 'missionStatement', to: '/mission-statement' },
    { key: 'membership', to: '/membership' },
    { key: 'newsletter', to: '/newsletter' },
    { key: 'events', to: '/events' },
    { key: 'resources', to: '/resources' },
    { key: 'staff', to: '/staff' },
    { key: 'contact', to: '/contact' },
  ];

  const navClass = ({ isActive }: { isActive: boolean }) =>
    `relative py-2 text-[13px] font-semibold transition-colors ${
      isActive
        ? 'text-[#2457a7] after:absolute after:inset-x-0 after:-bottom-1 after:h-0.5 after:bg-[#d95f75]'
        : 'text-[#4b5565] hover:text-[#172033]'
    }`;

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-black/5 bg-white/95 backdrop-blur-xl">
      <div className="page-container">
        <div className="flex h-[88px] items-center justify-between gap-5">
          <Link to="/" className="flex min-w-0 items-center gap-3" aria-label="JAJ home">
            <img
              src="/LOGO.png"
              alt="JAJ Logo"
              className="h-14 w-14 flex-none rounded-full object-cover shadow-[0_5px_15px_rgba(23,32,51,0.14)] sm:h-16 sm:w-16"
            />
            <div className="hidden min-w-0 sm:block">
              <p className="max-w-[285px] text-[17px] font-extrabold leading-tight text-[#172033]">
                Japanese Association of Jacksonville
              </p>
              <p className="mt-0.5 text-xs font-semibold uppercase text-[#d95f75]">
                Florida
              </p>
            </div>
          </Link>

          <nav className="hidden items-center gap-4 xl:flex" aria-label="Primary navigation">
            {navItems.map((item) => (
              <NavLink key={item.key} to={item.to} className={navClass}>
                {t(`nav.${item.key}`)}
              </NavLink>
            ))}
          </nav>

          <div className="flex flex-none items-center gap-2 sm:gap-3">
            <button
              onClick={toggleLanguage}
              className="inline-flex h-10 items-center gap-2 border border-[#e3e7ec] bg-[#f7f8fa] px-3 text-xs font-bold text-[#4b5565] transition hover:border-[#c3ccd7] hover:bg-white"
              aria-label="Toggle language"
              title={i18n.language === 'en' ? 'Switch to Japanese' : 'Switch to English'}
            >
              <span className="h-3 w-3 rounded-full bg-[#bc002d] shadow-[0_0_0_4px_white]" />
              <span>{i18n.language === 'en' ? '日本語' : 'EN'}</span>
            </button>

            <Link to="/contact" className="btn-primary hidden sm:inline-flex">
              <Heart className="h-4 w-4" />
              Support JAJ
            </Link>

            <button
              onClick={() => setIsMenuOpen((open) => !open)}
              className="icon-button xl:hidden"
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMenuOpen}
            >
              {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>

      {isMenuOpen && (
        <div className="border-t border-[#e3e7ec] bg-white xl:hidden">
          <nav className="page-container grid max-h-[calc(100dvh-89px)] grid-cols-2 gap-px overflow-y-auto bg-[#e3e7ec] py-px" aria-label="Mobile navigation">
            {navItems.map((item) => (
              <NavLink
                key={item.key}
                to={item.to}
                className={({ isActive }) =>
                  `bg-white px-4 py-4 text-sm font-bold transition ${
                    isActive ? 'text-[#2457a7]' : 'text-[#4b5565] hover:text-[#172033]'
                  }`
                }
              >
                {t(`nav.${item.key}`)}
              </NavLink>
            ))}
            <Link to="/contact" className="col-span-2 btn-primary my-3">
              <Heart className="h-4 w-4" />
              Support JAJ
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
